import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, IconButton, Menu, MenuItem, Fab, CircularProgress,
  Chip, Tooltip, Divider, Collapse,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import {
  getDocuments, getDocumentFolders, deleteDocument, deleteDocumentFolder, updateDocument, updateDocumentFlags,
  getDocumentSignedUrl,
} from '../services/documents';
import { isMarkdown, isImage } from './DocumentViewPage';
import type { StashDocument, DocumentFolder } from '../types';
import { DocFlags } from '../types';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadDocumentDialog from '../components/documents/UploadDocumentDialog';
import RenameDialog from '../components/documents/RenameDialog';
import FolderDialog from '../components/documents/FolderDialog';
import MoveDocumentDialog from '../components/documents/MoveDocumentDialog';

type FolderView = 'all' | 'starred' | string;

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [docs, setDocs] = useState<StashDocument[]>([]);
  const [view, setView] = useState<FolderView>('all');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameDoc, setRenameDoc] = useState<StashDocument | null>(null);
  const [moveDoc, setMoveDoc] = useState<StashDocument | null>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editFolder, setEditFolder] = useState<DocumentFolder | null>(null);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderMenuTarget, setFolderMenuTarget] = useState<DocumentFolder | null>(null);
  const [docMenuAnchor, setDocMenuAnchor] = useState<null | HTMLElement>(null);
  const [docMenuTarget, setDocMenuTarget] = useState<StashDocument | null>(null);
  const [foldersExpanded, setFoldersExpanded] = useState(() => localStorage.getItem('docs_foldersExpanded') !== 'false');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('docs_sidebarOpen') !== 'false');

  const loadFolders = useCallback(async () => {
    const f = await getDocumentFolders();
    setFolders(f);
  }, []);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        view === 'all' ? undefined :
        view === 'starred' ? { starred: true } :
        { folderId: view };
      const d = await getDocuments(params);
      setDocs(d);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDeleteDoc = async (doc: StashDocument) => {
    if (!confirm(`Delete "${doc.name}"?`)) return;
    await deleteDocument(doc.id);
    loadDocs();
  };

  const handleDeleteFolder = async (folder: DocumentFolder) => {
    if (!confirm(`Delete folder "${folder.name}"? Documents will become ungrouped.`)) return;
    await deleteDocumentFolder(folder.id);
    loadFolders();
    if (view === folder.id) setView('all');
  };

  const handleOpen = async (doc: StashDocument) => {
    if (isMarkdown(doc) || isImage(doc)) {
      navigate(`/documents/${doc.id}/view`);
    } else {
      const url = await getDocumentSignedUrl(doc.id, 'inline');
      window.open(url, '_blank');
    }
  };

  const handleDownload = async (doc: StashDocument) => {
    const url = await getDocumentSignedUrl(doc.id, 'attachment');
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    a.click();
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left panel */}
      <Box sx={{ width: sidebarOpen ? 220 : 48, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'auto', transition: 'width 0.2s' }}>
        <Box sx={{ p: sidebarOpen ? 1.5 : 0.5, display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton size="small" onClick={() => setSidebarOpen(prev => { const next = !prev; localStorage.setItem('docs_sidebarOpen', String(next)); return next; })}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {sidebarOpen && <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, ml: 1 }}>Folders</Typography>}
          {sidebarOpen && (
            <Tooltip title="New folder">
              <IconButton size="small" onClick={() => { setEditFolder(null); setFolderDialogOpen(true); }}>
                <CreateNewFolderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <List dense disablePadding>
          {[{ id: 'all', label: 'All Documents' }, { id: 'starred', label: 'Starred', starred: true }].map((v) => (
            <ListItem key={v.id} disablePadding>
              <Tooltip title={sidebarOpen ? '' : v.label} placement="right">
                <ListItemButton selected={view === v.id} onClick={() => setView(v.id as FolderView)} sx={{ borderRadius: 1, mx: 0.5, justifyContent: sidebarOpen ? 'initial' : 'center' }}>
                  <ListItemIcon sx={{ minWidth: sidebarOpen ? 32 : 'auto' }}>
                    {v.starred
                      ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                      : view === v.id ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary={v.label} primaryTypographyProps={{ variant: 'body2' }} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        {sidebarOpen && folders.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListItemButton
              onClick={() => setFoldersExpanded(prev => { const next = !prev; localStorage.setItem('docs_foldersExpanded', String(next)); return next; })}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <ListItemText primary="My Folders" primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }} />
              {foldersExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            </ListItemButton>
            <Collapse in={foldersExpanded} timeout="auto" unmountOnExit>
              <List dense disablePadding>
                {folders.map((f) => (
                  <ListItem key={f.id} disablePadding secondaryAction={
                    <IconButton size="small" edge="end" onClick={(e) => { setFolderMenuAnchor(e.currentTarget); setFolderMenuTarget(f); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }>
                    <ListItemButton selected={view === f.id} onClick={() => setView(f.id)} sx={{ borderRadius: 1, mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {view === f.id ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </Box>

      {/* Right panel */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {view === 'all' ? 'All Documents' : view === 'starred' ? 'Starred' : folders.find(f => f.id === view)?.name ?? 'Documents'}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : docs.length === 0 ? (
          <Typography color="text.secondary">No documents here yet.</Typography>
        ) : (
          <List>
            {docs.map((doc) => (
              <ListItem key={doc.id} divider secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Open">
                    <IconButton size="small" onClick={() => handleOpen(doc)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => {
                    const newFlags = (doc.flags & DocFlags.STARRED) ? doc.flags & ~DocFlags.STARRED : doc.flags | DocFlags.STARRED;
                    setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, flags: newFlags } : d));
                    updateDocumentFlags(doc.id, newFlags);
                  }} sx={{ color: (doc.flags & DocFlags.STARRED) ? 'warning.main' : 'action.disabled' }}>
                    {(doc.flags & DocFlags.STARRED) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { setDocMenuAnchor(e.currentTarget); setDocMenuTarget(doc); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              }>
                <ListItemIcon><InsertDriveFileIcon /></ListItemIcon>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <span>{formatBytes(doc.fileSize)}</span>
                      <span>·</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      <Chip label={doc.source} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* FAB — fixed lower right */}
      <Fab color="primary" size="medium" sx={{ position: 'fixed', bottom: 24, right: 24 }} onClick={() => setUploadOpen(true)}>
        <AddIcon />
      </Fab>

      {/* Folder context menu */}
      <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)} onClose={() => setFolderMenuAnchor(null)}>
        <MenuItem onClick={() => { setEditFolder(folderMenuTarget); setFolderDialogOpen(true); setFolderMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={() => { if (folderMenuTarget) handleDeleteFolder(folderMenuTarget); setFolderMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Doc context menu */}
      <Menu anchorEl={docMenuAnchor} open={Boolean(docMenuAnchor)} onClose={() => setDocMenuAnchor(null)}>
        <MenuItem onClick={() => { if (docMenuTarget) handleDownload(docMenuTarget); setDocMenuAnchor(null); }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>Download
        </MenuItem>
        <MenuItem onClick={() => { setRenameDoc(docMenuTarget); setDocMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={() => { setMoveDoc(docMenuTarget); setDocMenuAnchor(null); }}>
          <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>Move to folder
        </MenuItem>
        <MenuItem onClick={() => { if (docMenuTarget) handleDeleteDoc(docMenuTarget); setDocMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <UploadDocumentDialog open={uploadOpen} folders={folders} onClose={() => setUploadOpen(false)} onUploaded={() => { setUploadOpen(false); loadDocs(); }} />
      {renameDoc && (
        <RenameDialog open title="Rename document" currentName={renameDoc.name} onClose={() => setRenameDoc(null)}
          onSave={async (name) => { await updateDocument(renameDoc.id, { name }); setRenameDoc(null); loadDocs(); }} />
      )}
      {moveDoc && (
        <MoveDocumentDialog open folders={folders} currentFolderId={moveDoc.folderId} onClose={() => setMoveDoc(null)}
          onSave={async (folderId) => { await updateDocument(moveDoc.id, { folderId }); setMoveDoc(null); loadDocs(); }} />
      )}
      <FolderDialog open={folderDialogOpen} folder={editFolder}
        onClose={() => { setFolderDialogOpen(false); setEditFolder(null); }}
        onSaved={() => { setFolderDialogOpen(false); setEditFolder(null); loadFolders(); }} />
    </Box>
  );
}
